import { AtomicStatusType } from '../graphql/generated';
import { Recurrence, TransactionSchedulePlan } from '../types';
import { getFirstBusinessDay, isBusinessDay } from '../util/businessDays';
import { dt as dateTime, DT, dt } from '../util/dateTime';
import { isDaylightSavings } from '../util/daylightSavings';
import { getNextRecurrence } from '../util/recurrence';

export const getNextTransactionDateForPlan = (
    transactionSchedulePlan: TransactionSchedulePlan
): DT => {
    const dtNow = dt();
    const dtStart = transactionSchedulePlan.startDate
        ? dt(transactionSchedulePlan.startDate)
        : getNextAtomicProcessingDT(dtNow);
    return getNextTransactionDate({
        dtStart,
        recurrence: transactionSchedulePlan.recurrence,
        dtOnOrAfter: dtNow,
    });
};

export const getNextTransactionDate = ({
    dtStart,
    recurrence,
    dtOnOrAfter,
}: {
    dtStart: DT;
    recurrence: Recurrence;
    dtOnOrAfter: DT;
}): DT =>
    getNextAtomicProcessingDT(
        getNextRecurrence({ dtStart, recurrence, dtOnOrAfter })
    );

/**
 * Atomic's transaction processing hours are from 9am-1pm ET on business days.
 */
export const getNextAtomicProcessingDT = (dt: DT): DT => {
    // If it's a business day and before the cutoff time, return:
    // - the exact input, if input time is after opening hours; OR
    // - the opening time on the same day, if input time is before opening hours
    const closeTimeString = isDaylightSavings(dt)
        ? `17:00:00Z` //  10:00 PDT | 13:00 EDT | 17:00 UTC
        : `18:00:00Z`; // 10:00 PST | 13:00 EST | 18:00 UTC
    if (
        isBusinessDay(dt) &&
        dt.isBefore(dateTime(`${dt.toDateStr()}T${closeTimeString}`))
    ) {
        const openTimeString = isDaylightSavings(dt)
            ? `13:00:00Z` //  06:00 PDT | 09:00 EDT | 13:00 UTC
            : `14:00:00Z`; // 06:00 PST | 09:00 EST | 14:00 UTC
        return dt.isAfter(dateTime(`${dt.toDateStr()}T${openTimeString}`))
            ? dt
            : dateTime(`${dt.toDateStr()}T${openTimeString}`);
    }

    // Otherwise, return the first processing hour on the next business day
    const dtNextBusinessDay = getFirstBusinessDay(
        dt.add(1, 'day').startOf('day')
    );
    const openTimeString = isDaylightSavings(dtNextBusinessDay)
        ? `13:00:00Z` //  06:00 PDT | 09:00 EDT | 13:00 UTC
        : `14:00:00Z`; // 06:00 PST | 09:00 EST | 14:00 UTC
    return dateTime(`${dtNextBusinessDay.toDateStr()}T${openTimeString}`);
};

export const isPendingConsent = (status: AtomicStatusType) =>
    status === 'NEEDS_CONSENT' || status === 'NEEDS_ONBOARDING';
